import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  
  // Account deletion state
  const [deletionStep, setDeletionStep] = useState(0); // 0: initial, 1: waiting for OTP, 2: entering OTP
  const [deletionOtp, setDeletionOtp] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/profile", { name, phone });
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/profile/password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSavingPw(false); }
  };

  const requestAccountDeletion = async () => {
    setDeletingAccount(true);
    try {
      await api.post("/profile/request-deletion");
      setDeletionStep(2); // Move to OTP entry step
      toast.success("6-digit code sent to your WhatsApp");
    } catch (err) { 
      toast.error(extractError(err));
      setDeletingAccount(false);
    }
  };

  const confirmAccountDeletion = async () => {
    if (deletionOtp.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setDeletingAccount(true);
    try {
      await api.post("/profile/confirm-deletion", { otp: deletionOtp });
      toast.success("Account deleted successfully");
      // Log out and redirect
      await logout();
      navigate("/login");
    } catch (err) { 
      toast.error(extractError(err));
      setDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Account</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">My profile</h1>
      </div>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Personal details</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
            <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1.5 h-11 bg-zinc-50" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" data-testid="profile-name-input" /></div>
              <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11" data-testid="profile-phone-input" /></div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={savingProfile} className="bg-[#1A1A1A] text-white hover:bg-black rounded-full px-6" data-testid="profile-save-btn">{savingProfile ? "Saving…" : "Save"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border-black/10">
        <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Change password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={savePassword} className="space-y-4" data-testid="password-form">
            <div><Label>Current password</Label><Input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5 h-11" data-testid="current-password-input" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>New password</Label><Input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5 h-11" data-testid="new-password-input" /></div>
              <div><Label>Confirm new password</Label><Input type="password" required minLength={8} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5 h-11" data-testid="confirm-password-input" /></div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={savingPw} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full px-6" data-testid="password-save-btn">{savingPw ? "Updating…" : "Update password"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-red-50 border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-red-900 font-medium">
            <AlertTriangle size={16} />
            Delete account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {deletionStep === 0 && (
            <>
              <div className="text-sm text-red-800">
                Deleting your account will permanently remove all your data. This action cannot be undone.
              </div>
              <Button 
                onClick={requestAccountDeletion}
                disabled={deletingAccount}
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6"
                data-testid="request-deletion-btn"
              >
                {deletingAccount ? "Sending…" : "Delete my account"}
              </Button>
            </>
          )}

          {deletionStep === 2 && (
            <>
              <div className="text-sm text-red-800">
                Enter the 6-digit code we sent to your WhatsApp to confirm account deletion.
              </div>
              <div>
                <Label htmlFor="deletion_otp">Deletion code</Label>
                <Input 
                  id="deletion_otp"
                  inputMode="numeric"
                  maxLength={6}
                  value={deletionOtp}
                  onChange={(e) => setDeletionOtp(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 h-11 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  data-testid="deletion-otp-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => setDeletionStep(0)}
                  variant="outline"
                  className="flex-1 rounded-full"
                  data-testid="cancel-deletion-btn"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmAccountDeletion}
                  disabled={deletingAccount || deletionOtp.length !== 6}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                  data-testid="confirm-deletion-btn"
                >
                  {deletingAccount ? "Deleting…" : "Confirm deletion"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
